import { FilePdfOutlined } from '@ant-design/icons'
import type { ResidentItem } from '@lmc/ts-common'
import { Button, NavigationModal } from '@lmc/ui-kit'
import { type SexCode, SexDescriptionEnum, SexEnum } from '@lmc/validations'
import { gaEvent } from 'common/services/analytics'
import { getJSON } from 'common/services/api'
import { downloadPdfData } from 'common/utils'
import type { ResidentUpdateBody } from 'hooks/useResident'
import { useRouter } from 'hooks/useRouter'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getSuggestedSexForHospitalPassportPrompt } from '../utils/suggestedSexForHospitalPassport'

type HospitalPassportDownloadButtonProps = {
  hideIcon?: boolean
  isModsDemographicsEnabled: boolean
  isUpdating: boolean
  pdfTitle: string
  refetch: () => void | Promise<unknown>
  resident: ResidentWithDemographics
  residentId: string
  testId?: string
  updateResident: (values: ResidentUpdateBody) => Promise<unknown>
  url: string
}

type PromptState =
  | { kind: 'confirmSuggested'; suggested: SexCode }
  | { kind: 'missingSex' }
  | null

// TODO(cm-2815): Refactor to use the `@lmc/validations` resident type end-to-end (and update `useResident`)
// so we can drop this shim and stop intersecting with legacy `@lmc/ts-common` types.
type ResidentWithDemographics = ResidentItem & {
  demographics?: {
    sex?: SexCode | null
    genderIdentity?: string | null
  } | null
}

async function downloadHospitalPassportPdf(
  pdfTitle: string,
  url: string
): Promise<void> {
  const res = await getJSON({ url, responseType: 'arrayBuffer' })
  downloadPdfData(res, pdfTitle)
}

export const HospitalPassportDownloadButton = ({
  hideIcon,
  isModsDemographicsEnabled,
  isUpdating,
  pdfTitle,
  refetch,
  resident,
  residentId,
  testId,
  updateResident,
  url
}: HospitalPassportDownloadButtonProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const { navigate } = useRouter()

  const [isPdfLoading, setIsPdfLoading] = useState(false)
  const [prompt, setPrompt] = useState<PromptState>(null)

  const loading = isPdfLoading || isUpdating

  const commonTooltip = t(supConstants.SEX_PHENOTYPIC_INFO_TOOLTIP)

  const confirmSuggestedSexAndDownload = async (
    suggested: SexCode
  ): Promise<void> => {
    try {
      await updateResident({ demographics: { sex: suggested } })
    } catch {
      return
    }
    await Promise.resolve(refetch())
    await runDownload()
  }

  const goToPersonalInformation = (): void => {
    navigate(`/residents/${residentId}/personal-information`)
  }

  const handleClick = (): void => {
    if (!isModsDemographicsEnabled) {
      void runDownload()
      return
    }

    const demographics = resident.demographics
    const sex = demographics?.sex
    // If sex is known (male/female/indeterminate), download as normal.
    // If sex is missing OR "not known / not recorded", prompt to update but allow "download anyway".
    if (sex != null && sex !== SexEnum.notKnownNotRecorded) {
      void runDownload()
      return
    }

    const suggested = getSuggestedSexForHospitalPassportPrompt({
      genderIdentity: resident.demographics?.genderIdentity,
      legacyGender: resident.gender
    })

    // If sex is explicitly "not recorded", we prompt to update but allow "download anyway"
    if (sex === SexEnum.notKnownNotRecorded) {
      if (suggested) {
        setPrompt({ kind: 'confirmSuggested', suggested })
        return
      }

      setPrompt({ kind: 'missingSex' })
      return
    }

    if (suggested == null) {
      setPrompt({ kind: 'missingSex' })
      return
    }

    setPrompt({ kind: 'confirmSuggested', suggested })
  }

  const handleModalCancelButton = (): void => {
    setPrompt(null)
    if (prompt?.kind === 'missingSex') {
      void runDownload()
      return
    }
    goToPersonalInformation()
  }

  const handleModalConfirm = (): void => {
    setPrompt(null)
    if (prompt?.kind === 'missingSex') {
      goToPersonalInformation()
      return
    }
    if (prompt?.kind === 'confirmSuggested') {
      void confirmSuggestedSexAndDownload(prompt.suggested)
    }
  }

  const navigationModalProps = (() => {
    if (!prompt) return null

    if (prompt.kind === 'missingSex') {
      return {
        modalCancelText: t(
          supConstants.HOSPITAL_PASSPORT_SEX_MISSING_CANCEL_DOWNLOAD_ANYWAY
        ),
        modalConfirmText: t(
          supConstants.HOSPITAL_PASSPORT_SEX_MISSING_OK_UPDATE
        ),
        modalContentText: t(supConstants.HOSPITAL_PASSPORT_SEX_MISSING_BODY),
        modalTitle: t(supConstants.HOSPITAL_PASSPORT_SEX_MISSING_TITLE)
      }
    }

    return {
      modalCancelText: t(supConstants.HOSPITAL_PASSPORT_CONFIRM_SEX_CANCEL_NO),
      modalConfirmText: t(supConstants.HOSPITAL_PASSPORT_CONFIRM_SEX_OK),
      modalContentText: t(supConstants.HOSPITAL_PASSPORT_CONFIRM_SEX_BODY, {
        sex: SexDescriptionEnum[prompt.suggested]
      }),
      modalTitle: t(supConstants.HOSPITAL_PASSPORT_CONFIRM_SEX_TITLE)
    }
  })()

  const runDownload = async (): Promise<void> => {
    setIsPdfLoading(true)
    try {
      await downloadHospitalPassportPdf(pdfTitle, url)
      gaEvent({
        category: 'PDF Download',
        action: 'Hospital passport',
        label: ''
      })
    } finally {
      setIsPdfLoading(false)
    }
  }

  return (
    <>
      <Button
        dataTestId={
          testId ? `${testId}-pdf-export` : 'hospital-passport-pdf-export'
        }
        isLoading={loading}
        onClick={handleClick}
        variant="default"
      >
        {!hideIcon && <FilePdfOutlined />}
        {t(supConstants.HOSPITAL_PASSPORT)}
      </Button>
      {navigationModalProps && (
        <NavigationModal
          cancelNavigation={() => setPrompt(null)}
          confirmButtonProps={{ variant: 'primary' }}
          confirmNavigation={handleModalConfirm}
          maskClosable={false}
          modalTitleTooltip={commonTooltip}
          onCancelNavigation={handleModalCancelButton}
          showPrompt
          {...navigationModalProps}
        />
      )}
    </>
  )
}
